import { Button, Container, Grid, Paper, Typography } from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { ChevronRight } from "@material-ui/icons";
import EmailIcon from "@material-ui/icons/Email";
import { App } from "app-interfaces";
import i18next from "i18next";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../../config/AppContext";
import { getLanguageFromLocalStorage, saveLanguageToLocalStorage } from "../../storage";
import { ConfirmModal, ContactUsForm, LanguageModal } from "../Modals";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    paper: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      textAlign: "left",
      color: theme.palette.text.secondary,
      "& h5": {
        color: "#9B9B9B",
        fontSize: "14px",
        padding: "5px 10px",
      },
      "& h6": {
        fontSize: "14px",
        lineHeight: "16px",
        marginTop: theme.spacing(3),
      },
      "& p": {
        color: theme.palette.text.primary,
        fontSize: "15px",
        lineHeight: "18px",
      },
      "& span": {
        display: "block",
        color: theme.palette.text.secondary,
        fontSize: "16px",
        lineHeight: "20px",
        "& svg": {
          float: "right",
        },
      },
    },
    actions: {
      marginTop: 20,
      "& button": {
        fontSize: 17,
        marginTop: 5,
        height: 56,
        backgroundColor: "var(--text-primary)",
        textTransform: "none",
        "& svg": {
          marginLeft: "calc(100% - 120px)",
        },
        "&:hover": {
          backgroundColor: "var(--text-primary)",
        },
      },
    },
    noWrap: {
      whiteSpace: "nowrap",
    },
  })
);

const GeneralComponent: React.FC = (props) => {
  const classes = useStyles();

  return (
    <div id={"General"}>
      <Grid container>
        <Grid item xs={12} md={8}>
          <Paper className={classes.paper}>
            <GeneralSection />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Container className={classes.actions}>
            <ActionSection />
          </Container>
        </Grid>
      </Grid>
    </div>
  );
};

const GeneralSection: React.FC = () => {
  const { t } = useTranslation();
  const { Methods, metric, settings } = useStateValue() as {
    Methods: App.Methods;
    settings: App.State["settings"];
    metric: App.State["metric"];
  };

  const [showConfirm, setShowConfirm] = React.useState<
    { message: string; title: string } | undefined
  >();

  const [showLanguage, setshowLanguage] = React.useState<
    { list: string; value: string } | undefined
  >();
  const formatLanguage = (language?: string) => {
    switch (language) {
      case "en":
        return "English";
      case "dn":
        return "Danish";
      case "sv":
        return "Swedish";
      case "nb":
        return "Norwegian";
      default:
        return "English";
    }
  };
  const toggleMetrics = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const message = `${t("Switch to")} ${
      !metric ? t("Metric (kilograms/kilometers)") : t("Imperial (pounds/miles)")
    }`;
    const title = t("Confirm");
    return setShowConfirm({ message, title });
  };

  const toggleLanguage = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const title = t("Confirm");
    return setshowLanguage({ list: title, value: "Language" });
  };

  const handleConfirm = () => {
    if (!showConfirm) return;
    Methods.setAppState({ metric: !metric });
    return setShowConfirm(undefined);
  };

  const handleLanguage = async (language: any) => {
    saveLanguageToLocalStorage(language);
    i18next.changeLanguage(language);
    try {
      await Methods.updateProfile({
        ...settings!,
        language,
      });
    } catch (error) {
      // TODO handle error saving language
      console.error(error);
    }
    if (!showLanguage) return;
    return setshowLanguage(undefined);
  };

  useEffect(() => {
    const language = getLanguageFromLocalStorage() || i18next.language;
    i18next.changeLanguage(language, (err, t) => {
      t(language);
    });
  }, [showLanguage]);

  return (
    <>
      <Grid container onClick={toggleLanguage} spacing={3} style={{ cursor: "pointer" }}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Language")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography id="measures" variant="body2" component="span" noWrap>
            {settings && t(`${formatLanguage(i18next.language)}`)}&nbsp;
            <ChevronRight />
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3} onClick={toggleMetrics} style={{ cursor: "pointer" }}>
        <Grid item xs={6}>
          <Typography variant="body1">{t("Measurement units")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography id="measures" variant="body2" component="span" noWrap>
            {metric ? t("Metric (kilograms, kilometers)") : t("Imperial (pounds, miles)")}
            <ChevronRight />
          </Typography>
        </Grid>
      </Grid>

      <LanguageModal
        chosenLanguage={i18next.language}
        show={!!showLanguage}
        list={showLanguage && showLanguage.list}
        title={showLanguage && t(`${showLanguage.value}`)}
        onSubmit={(e: any) => handleLanguage(e)}
        onHide={() => setshowLanguage(undefined)}
      />

      <ConfirmModal
        show={!!showConfirm}
        message={showConfirm && showConfirm.message}
        title={showConfirm && showConfirm.title}
        onSubmit={handleConfirm}
        onHide={() => setShowConfirm(undefined)}
      />
    </>
  );
};

const ActionSection: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { Methods } = useStateValue() as { Methods: App.Methods };
  const [contactUs, showContactUs] = React.useState(false);
  const handleContact = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const params = {};
    formData.forEach((v, k) => Object.assign(params, { [k]: v }));
    Methods.contactUs(params);
    return showContactUs(false);
  };

  return (
    <>
      <Button
        fullWidth
        className={classes.noWrap}
        variant="contained"
        color="primary"
        onClick={() => showContactUs(true)}
      >
        {t("Contact Us")} <EmailIcon />
      </Button>
      <ContactUsForm
        open={contactUs}
        onClose={() => showContactUs(false)}
        onSubmit={handleContact}
      />
    </>
  );
};
export default GeneralComponent;
